/**
 * Преобразование xpointer ↔ DOM-позиция.
 *
 * Единый модуль для полного цикла:
 *   xpointer-строка → {elementId, charIndex} → DOM {node, offset}
 *   и обратно.
 *
 * Низкоуровневые утилиты (мягкие переносы, текстовые узлы) — в char-position.ts.
 */

import type { Xpointer } from "lib/types";

import {
  charIndexToDomPosition,
  domPositionToCharIndex,
} from "./char-position";
import { extractVisibleTextFromElement } from "./text-content";

export type DomPosition = { node: Text; offset: number };

/**
 * Находит ближайший предок с id (якорный элемент для xpointer).
 */
export function findXpointerAnchor(node: Node): Element | null {
  let el =
    node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
  while (el && !el.id) {
    el = el.parentElement;
  }
  return el;
}

/**
 * Парсит xpointer-строку в elementId + charIndex.
 * Формат: "/1/4/63.0/1.42" → { elementId: "/1/4/63.0", charIndex: 42 }
 */
export function parseXpointer(xpointer: string): {
  elementId: string;
  charIndex: number;
} | null {
  const match = xpointer.match(/^(.+)\/\d+\.(\d+)$/);
  if (!match) return null;

  const pathPart = match[1];
  const elementId = pathPart.endsWith(".0") ? pathPart : `${pathPart}.0`;

  return {
    elementId,
    charIndex: parseInt(match[2], 10),
  };
}

function formatXpointer(elementId: string, charIndex: number): string {
  return `${elementId}/1.${charIndex}`;
}

/**
 * xpointer-строка → DOM-элемент + позиция в нём.
 */
export function resolveXpointer(xpointer: string): {
  element: Element;
  charIndex: number;
  position: DomPosition;
} | null {
  const parsed = parseXpointer(xpointer);
  if (!parsed) return null;

  const element = document.getElementById(parsed.elementId);
  if (!element) return null;

  const position = charIndexToDomPosition(element, parsed.charIndex);
  if (!position) return null;

  return { element, charIndex: parsed.charIndex, position };
}

function advancePastWhitespace(text: string, startIndex: number): number {
  let index = startIndex;
  while (index < text.length && /\s/.test(text[index])) {
    index++;
  }
  return index;
}

/**
 * Разрешает пару xpointer-строк в DOM Range.
 *
 * Обрабатывает три случая:
 * — start === end: Range покрывает всё содержимое элемента
 * — один и тот же элемент: Range внутри элемента (с расширением на пробелы в конце)
 * — разные элементы: Range от start до end через границы элементов
 *
 * Индекс символа конца в xpointer-формате инклюзивный; здесь конвертируется в эксклюзивный.
 */
export function resolveXpointerRange(
  startXpointer: string,
  endXpointer: string
): Range | null {
  if (startXpointer === endXpointer) {
    const el = document.getElementById(startXpointer);
    if (!el) return null;
    const range = document.createRange();
    range.selectNodeContents(el);
    return range;
  }

  const start = resolveXpointer(startXpointer);
  if (!start) return null;

  const endParsed = parseXpointer(endXpointer);
  if (!endParsed) return null;

  const endElement = document.getElementById(endParsed.elementId);
  if (!endElement) return null;

  const range = document.createRange();
  range.setStart(start.position.node, start.position.offset);

  if (start.element === endElement) {
    const visibleText = extractVisibleTextFromElement(endElement);
    const endCharIndex = advancePastWhitespace(
      visibleText,
      endParsed.charIndex + 1
    );
    const endPos = charIndexToDomPosition(endElement, endCharIndex);
    if (!endPos) return null;
    range.setEnd(endPos.node, endPos.offset);
  } else {
    const endPos = charIndexToDomPosition(endElement, endParsed.charIndex + 1);
    if (!endPos) return null;
    range.setEnd(endPos.node, endPos.offset);
  }

  return range;
}

/**
 * DOM-позиция (узел + смещение) → xpointer-строка.
 * Обратная операция к resolveXpointer.
 *
 * Для Text-узлов offset — индекс символа.
 * Для Element-узлов offset — индекс дочернего узла; находим ближайший
 * текстовый узел, чтобы получить корректный charIndex.
 */
export function domPositionToXpointer(
  node: Node,
  offset: number
): string | null {
  if (node.nodeType === Node.TEXT_NODE) {
    const element = findXpointerAnchor(node);
    if (!element) return null;
    const charIndex = domPositionToCharIndex(element, node as Text, offset);
    return formatXpointer(element.id, charIndex);
  }

  const textPos = resolveElementPosition(node, offset);

  if (!textPos) {
    // Контейнер без текста (напр. <span><img></span>).
    // Ищем ближайший текстовый узел перед контейнером в порядке документа.
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT
    );
    walker.currentNode = node;
    const textNode = walker.previousNode() as Text | null;
    if (!textNode) return null;
    const element = findXpointerAnchor(textNode);
    if (!element) return null;
    const exclusiveIndex = domPositionToCharIndex(
      element,
      textNode,
      textNode.length
    );
    // Конвертируем эксклюзивный конец в инклюзивный индекс для xpointer
    const charIndex = Math.max(0, exclusiveIndex - 1);
    return formatXpointer(element.id, charIndex);
  }

  const element = findXpointerAnchor(textPos.node);
  if (!element) return null;
  const charIndex = domPositionToCharIndex(
    element,
    textPos.node,
    textPos.offset
  );
  return formatXpointer(element.id, charIndex);
}

/**
 * Range → пара xpointer-строк (start, end).
 *
 * Инкапсулирует разницу между Text- и Element-контейнерами:
 * для Text endOffset — эксклюзивный индекс символа (конвертируем в инклюзивный),
 * для Element endOffset — эксклюзивная граница дочерних узлов (используем как есть).
 */
export function rangeToXpointers(
  range: Range
): { start: Xpointer; end: Xpointer } | null {
  const start = domPositionToXpointer(range.startContainer, range.startOffset);

  const endOffset =
    range.endContainer.nodeType === Node.TEXT_NODE
      ? Math.max(0, range.endOffset - 1)
      : range.endOffset;
  const end = domPositionToXpointer(range.endContainer, endOffset);

  if (!start || !end) return null;
  return { start, end };
}

function resolveElementPosition(
  container: Node,
  childOffset: number
): { node: Text; offset: number } | null {
  const children = container.childNodes;
  const clampedOffset = Math.min(Math.max(0, childOffset), children.length);

  for (let i = clampedOffset - 1; i >= 0; i--) {
    const textNode = findLastTextNode(children[i]);
    if (textNode) return { node: textNode, offset: textNode.length };
  }

  for (let i = clampedOffset; i < children.length; i++) {
    const textNode = findFirstTextNode(children[i]);
    if (textNode) return { node: textNode, offset: 0 };
  }

  return null;
}

function findFirstTextNode(root: Node): Text | null {
  if (root.nodeType === Node.TEXT_NODE) return root as Text;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  return walker.nextNode() as Text | null;
}

function findLastTextNode(root: Node): Text | null {
  if (root.nodeType === Node.TEXT_NODE) return root as Text;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let last: Text | null = null;
  let node = walker.nextNode() as Text | null;
  while (node) {
    last = node;
    node = walker.nextNode() as Text | null;
  }
  return last;
}
